/**
 * Module dependencies.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { Title } from 'app/components/atoms/title/title';
import { Spinner } from 'app/components/ui/spinner';
import { Button } from 'app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'app/components/ui/dialog';
import { InputField } from 'app/components/atoms/forms/fields/input/input-field';
import { SubmitButton } from 'app/components/atoms/forms/submit-button/submit-button';
import { useToast } from 'app/components/providers/toast-provider/toast-provider';
import { ChevronRight, Layers, PiggyBank, Plus, Users } from 'lucide-react';
import { appRoutes } from 'app/routes';
import { resolveAppPath } from 'app/core/utils/url-resolver';
import { useCreatePoolInvestProject, usePoolInvestProjects } from 'app/hooks/requests/pool-invest/use-pool-invest';

/**
 * Create-project dialog.
 */

function CreateProjectDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const [translate] = useTranslation();
  const { success, error } = useToast();
  const create = useCreatePoolInvestProject();

  const {
    control,
    handleSubmit,
    reset,
    formState: { isSubmitting }
  } = useForm({ mode: 'all', defaultValues: { name: '', description: '' } });

  const close = () => {
    reset();
    onOpenChange(false);
  };

  const onSubmit = async (values: any) => {
    try {
      await create.mutateAsync({ name: values.name, description: values.description || null });

      success({ message: translate('poolInvest.project.created') });
      close();
    } catch (err: any) {
      error({ message: err?.response?.data?.data?.message ?? err?.response?.data?.data ?? 'Error' });
    }
  };

  return (
    <Dialog onOpenChange={value => (value ? onOpenChange(true) : close())} open={open}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate('poolInvest.project.add')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={'space-y-4'}>
            <InputField
              control={control}
              label={translate('poolInvest.form.name')}
              name={'name'}
              rules={{ required: true }}
            />

            <InputField
              control={control}
              inputType={'textArea'}
              label={translate('poolInvest.form.description')}
              name={'description'}
              rows={4}
            />
          </div>

          <SubmitButton label={translate('poolInvest.form.save')} loading={isSubmitting} />
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Export `PoolInvestProjects` component.
 *
 * Overview of the shared investment pools the user can see, rendered as cards,
 * with a create action.
 */

export function PoolInvestProjects(): JSX.Element {
  const [translate] = useTranslation();
  const { data, isLoading } = usePoolInvestProjects();
  const [open, setOpen] = useState(false);

  const projects = data ?? [];

  return (
    <div className={'w-full space-y-5'}>
      <div className={'flex flex-wrap items-baseline justify-between gap-3'}>
        <Title>{translate('poolInvest.title')}</Title>

        <Button onClick={() => setOpen(true)}>
          <Plus className={'h-4 w-4'} />
          {translate('poolInvest.project.add')}
        </Button>
      </div>

      {isLoading ? (
        <div className={'grid place-items-center py-20'}>
          <Spinner />
        </div>
      ) : projects.length === 0 ? (
        <div
          className={
            'flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-muted/30 px-6 py-16 text-center'
          }
        >
          <div className={'grid h-12 w-12 place-items-center rounded-full bg-muted text-muted-foreground'}>
            <PiggyBank className={'h-6 w-6'} />
          </div>

          <p className={'mt-4 text-base font-semibold text-foreground'}>{translate('poolInvest.empty.title')}</p>
          <p className={'mt-1 max-w-[360px] text-sm text-muted-foreground'}>
            {translate('poolInvest.empty.description')}
          </p>
        </div>
      ) : (
        <div className={'grid grid-cols-3 gap-4 max-[991px]:grid-cols-2 max-[600px]:grid-cols-1'}>
          {projects.map(project => (
            <Link
              className={
                'group rounded-xl border border-border bg-card p-5 shadow-sm transition-shadow hover:shadow-md'
              }
              key={project.id}
              to={resolveAppPath(appRoutes.dashboard.poolInvest.project, { projectId: project.id })}
            >
              <div className={'flex items-start justify-between gap-3'}>
                <span className={'grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary'}>
                  <PiggyBank className={'h-5 w-5'} />
                </span>

                <ChevronRight
                  className={'h-4 w-4 text-muted-foreground/50 transition-transform group-hover:translate-x-0.5'}
                />
              </div>

              <div className={'mt-3 truncate text-base font-semibold text-foreground'}>{project.name}</div>

              {project.description ? (
                <div className={'mt-1 line-clamp-2 text-sm text-muted-foreground'}>{project.description}</div>
              ) : null}

              <div className={'mt-3 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground'}>
                <span className={'inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-medium'}>
                  <Users className={'h-3 w-3'} />
                  {project.participantCount}
                </span>

                <span className={'inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-medium'}>
                  <Layers className={'h-3 w-3'} />
                  {project.bucketCount}
                </span>

                <span className={'rounded-full bg-muted px-2 py-0.5 font-medium'}>{project.currency}</span>
              </div>
            </Link>
          ))}
        </div>
      )}

      <CreateProjectDialog onOpenChange={setOpen} open={open} />
    </div>
  );
}
